import React, { useState, useEffect } from 'react'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import ButtonGroup from './ButtonGroup'
import HoldValidationMessage from './HoldValidationMessage'
import { canHoldRequest } from './holdValidation'
import {
  formatDateToMMDDYYYY,
  convertMMDDYYYYToYYYYMMDD,
  isISODateInPast,
} from '../../../utils/date'
import {
  RADIO_OPTIONS,
  PARTIALLY_APPROVED_OPTION,
  APPLY_REQUEST_STATUS_HEADING,
} from './radioOptions'
import { ButtonStateConfig } from './buttonStateUtils'
import {
  getRadioButtonState,
  shouldHidePartiallyApprovedOption,
} from './radioButtonUtils'

interface HoldDetails {
  holdPlacedOn: string
  holdPlacedBy: string
  approverComments: string
}

interface HoldTimeOffFormProps {
  onSubmit?: (data: {
    status: string
    holdFrom?: string
    holdUntil?: string
    comments?: string
  }) => void
  onCancel?: () => void
  onStatusChange?: (status: string) => void
  isOnHold?: boolean
  holdDetails?: HoldDetails
  isRetracted?: boolean
  numberOfDates?: number
  startDate?: string
  activeForm?: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  isDisabledDueToDate?: boolean
  buttonStateConfig?: ButtonStateConfig
  holdUntilMaxDate?: string | null
  onHoldEndDate?: string | null
  toTypeCd?: string
  disableEndDate?: string
  dateRequested?: string
  onHoldStartDate?: string | null
  isHoldAllowed?: boolean
}
type SubmitData = {
  status: string
  holdStartDate?: string
  heldUntilDate?: string
  comments?: string

}

const MM_DD_YYYY_REGEX = /^\d{2}\/\d{2}\/\d{4}$/

const getTodayDate = (): string => {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const HoldTimeOffForm: React.FC<HoldTimeOffFormProps> = ({
  onSubmit,
  onCancel,
  onStatusChange,
  isOnHold = false,
  holdDetails,
  isRetracted = false,
  numberOfDates = 1,
  startDate = '',
  activeForm = 'approved',
  isDisabledDueToDate = false,
  buttonStateConfig,
  holdUntilMaxDate,
  onHoldEndDate,
  toTypeCd,
  disableEndDate,
  dateRequested,
  onHoldStartDate,
  isHoldAllowed = true,
}) => {
  // Calculate if Hold can be applied based on dateRequested
  // If already on hold, allow 'hold' selection regardless of date
  const canHold = isOnHold || canHoldRequest(startDate, toTypeCd)

  const [selectedStatus, setSelectedStatus] = useState(
    isOnHold ? 'hold' : 'approved'
  )
  const [approverComments, setApproverComments] = useState(
    isOnHold && holdDetails ? holdDetails.approverComments : ''
  )
  const [holdFrom, setHoldFrom] = useState<string>(
    isOnHold ? (onHoldStartDate ?? getTodayDate()) : getTodayDate()
  )
  // Case 1/2: not on hold → use max date; Case 3/4: on hold → use existing hold end date
  const initialHoldUntil = isOnHold
    ? (onHoldEndDate ?? holdUntilMaxDate ?? getTodayDate())
    : (holdUntilMaxDate ?? getTodayDate())
  const [holdUntil, setHoldUntil] = useState<string>(initialHoldUntil)
  const [holdUntilRaw, setHoldUntilRaw] = useState<string>(
    formatDateToMMDDYYYY(initialHoldUntil)
  )
  const [holdUntilError, setHoldUntilError] = useState<string>('')
useEffect(() => {
  const nextDate = isOnHold
    ? (onHoldEndDate ?? holdUntilMaxDate ?? getTodayDate())
    : (holdUntilMaxDate ?? getTodayDate())
  setHoldUntil(nextDate)
  setHoldUntilRaw(formatDateToMMDDYYYY(nextDate))
}, [isOnHold, onHoldEndDate, holdUntilMaxDate])
useEffect(() => {
  setHoldFrom(isOnHold ? (onHoldStartDate ?? getTodayDate()) : getTodayDate())
}, [isOnHold, onHoldStartDate])

  // Sync selectedStatus with activeForm when it changes
  useEffect(() => {
    if (activeForm === 'hold') {
      setSelectedStatus('hold')
    }
  }, [activeForm])

  // Clear comments when switching away from hold to a different action
  useEffect(() => {
    if (isOnHold && selectedStatus !== 'hold') {
      setApproverComments('')
    }
  }, [selectedStatus, isOnHold])

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status)
    if (onStatusChange) {
      onStatusChange(status)
    }
  }

  const createSubmitData = (): SubmitData => {
    const submitData: SubmitData = {
      status: selectedStatus,
      comments: approverComments,
    }

    if (selectedStatus === 'hold') {
      submitData.holdStartDate = holdFrom
      submitData.heldUntilDate = holdUntil
    }

    return submitData
  }
  
   /**
   * Returns true if the given date is before today.
   * Time portion is ignored.
   */
  const isPastDate = (date?: string) => {
    return isISODateInPast(date)
  }
  // Disable when BOTH start and end dates are before today
  const areBothDatesInPast =
    isPastDate(dateRequested) && isPastDate(disableEndDate)
  
  // Case 4: on hold + isHoldAllowed=false → Hold Until field and submit are both disabled
  const isHoldUntilDisabled = isOnHold && !isHoldAllowed

  const handleSubmit = () => {
    if (selectedStatus === 'hold' && !isHoldUntilDisabled) {
      if (!holdUntilRaw) {
        setHoldUntilError('Please enter the Held Until date')
        return
      }
      if (!MM_DD_YYYY_REGEX.test(holdUntilRaw)) {
        setHoldUntilError('Invalid format')
        return
      }
      const holdUntilDate = new Date(`${holdUntil}T00:00:00`)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const maxDate = holdUntilMaxDate ? new Date(`${holdUntilMaxDate}T00:00:00`) : null
      // NaN (logically invalid date e.g. 13/32/2026) is treated the same as out-of-range
      if (Number.isNaN(holdUntilDate.getTime()) || holdUntilDate < today || (maxDate && holdUntilDate > maxDate)) {
        setHoldUntilError('Held until date is invalid')
        return
      }
    }
    setHoldUntilError('')
    onSubmit?.(createSubmitData())
  }
  const { labelColor: partiallyApprovedLabelColor } = getRadioButtonState(
    PARTIALLY_APPROVED_OPTION.value,
    buttonStateConfig,
    { canHold, isRetracted }
  )

  // When true, all radio buttons should be disabled (e.g., past dates in History Tab)
  const disableAllOptions = isDisabledDueToDate || areBothDatesInPast

  const renderRadioOption = (option: (typeof RADIO_OPTIONS)[number]) => {
    const { isDisabled: configDisabled, labelColor: configLabelColor } = getRadioButtonState(
      option.value,
      buttonStateConfig,
      { canHold, isRetracted }
    )
    const isDisabled = disableAllOptions || configDisabled
    const labelColor = isDisabled ? 'text-[#A0A5B6]' : configLabelColor

    return (
      <div key={option.id} className="flex items-center gap-3 mb-1!">
        <input
          type="radio"
          id={option.id}
          name="status"
          value={option.value}
          checked={selectedStatus === option.value}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={isDisabled}
          className="w-4 h-4"
        />

        <label
          htmlFor={option.id}
          className={`text-base font-light leading-6 tracking-[0.16px] cursor-pointer ${labelColor}`}
        >
          {option.label}
        </label>
      </div>
    )
  }

  return (
    <div className="px-6! pt-0! pb-6!">
      <p className="p4 -medium text-[#0D1C3D] m-0! pb-1!">
        {APPLY_REQUEST_STATUS_HEADING.text}
      </p>

      {/* Status Radio Buttons */}
      <div className="space-y-4 mb-8 pb-6!">
        {RADIO_OPTIONS.map(renderRadioOption)}
        {/* ✅ Hide partially approved option for retractions or single-day requests */}
        {!shouldHidePartiallyApprovedOption(isRetracted, numberOfDates) && (
          <div className="flex items-center gap-3">
            <input
              type="radio"
              id={PARTIALLY_APPROVED_OPTION.id}
              name="status"
              value={PARTIALLY_APPROVED_OPTION.value}
              checked={selectedStatus === PARTIALLY_APPROVED_OPTION.value}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={disableAllOptions || buttonStateConfig?.partiallyApprovedDisabled}
              className="w-4 h-4"
            />
            <label
              htmlFor={PARTIALLY_APPROVED_OPTION.id}
              className={`text-base font-light leading-6 tracking-[0.16px] cursor-pointer ${disableAllOptions || buttonStateConfig?.partiallyApprovedDisabled ? 'text-[#A0A5B6]' : partiallyApprovedLabelColor}`}
            >
              {PARTIALLY_APPROVED_OPTION.label}
            </label>
          </div>
        )}
      </div>

      {/* Hold Dates Section - Only show when Hold is selected, hide instructional text when already on hold */}
      {selectedStatus === 'hold' && (
        <div className="mb-6 pt-6 pb-6!">
          {!isOnHold && (
            <h4 className="text-[#0D1C3D] text-base font-light leading-6 tracking-[0.16px] mb-4">
              Time Off requests placed On Hold will be automatically released on
              the date specified
            </h4>
          )}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6 md:gap-8 mt-4">
            <div className="w-full md:w-72">
              <Datepicker
                name="holdFrom"
                label="Hold From"
                openBehaviour="iconView"
                helperText="MM/DD/YYYY"
                value={formatDateToMMDDYYYY(holdFrom)}
                disabled
              />
            </div>
            <div className="w-full md:w-72">
              <Datepicker
                name="holdUntil"
                label="Hold Until"
                openBehaviour="iconView"
                helperText="MM/DD/YYYY"
                value={holdUntilRaw}
                disabled={isHoldUntilDisabled}
                disableAfterDate={
                  holdUntilMaxDate
                    ? new Date(
                        `${holdUntilMaxDate}T00:00:00`
                      ).toLocaleDateString('en-US')
                    : undefined
                }
                onChange={(_name, value) => {
                  // Datepicker emits MM/DD/YYYY; holdUntil is kept in YYYY-MM-DD
                  // since that's the format the status-update API validates against.
                  const picked = Array.isArray(value) ? value[0] : value || ''
                  setHoldUntilRaw(picked)
                  setHoldUntil(picked ? convertMMDDYYYYToYYYYMMDD(picked) : '')
                  if (picked) setHoldUntilError('')
                }}
                error={holdUntilError || undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hold Validation Message */}
      {!canHold &&           <HoldValidationMessage
            message={
              toTypeCd === 'A'
                ? 'Cannot Hold Request: Start date is less than 1 day'
                : 'Cannot Hold Request: Start date is less than 15 days'
            }
          />}

      {/* Approver Comments - Always show for user input, regardless of selected status */}
      <div className="mb-6 pt-6 pb-6!">
        <label htmlFor="comments" className="block p3 -medium mb-1">
          Approver Comments
        </label>
        <textarea
          id="comments"
          className="w-full border rounded p-2 min-h-20 disabled:bg-gray-100 disabled:cursor-not-allowed"
          maxLength={1000}
          value={approverComments}
          onChange={(e) => setApproverComments(e.target.value)}
          disabled={disableAllOptions}
        />
        <div className="text-left text-xs text-gray-500 mt-1">
          {1000 - approverComments.length} of 1000 characters left
        </div>
      </div>

      {/* Buttons - Always show */}
      <div className="pb-8!">
        <ButtonGroup
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitDisabled={isDisabledDueToDate || areBothDatesInPast || isHoldUntilDisabled}
        />
      </div>
    </div>
  )
}

export default HoldTimeOffForm
